import {FC, useContext, useCallback} from "react";
import Button from "@mui/material/Button";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogContentText from "@mui/material/DialogContentText";
import DialogTitle from "@mui/material/DialogTitle";
import {useSSTranslation} from "@/i18n/useSSTranslation";
import {SocketContext} from "@/contexts/socketContext";
import {palette} from "@/constants";
import {SOCKET_EVENTS, StartRescheduleOverdueModulesPayload, JobStatusCallback} from "@buildwitt/shapeshift-types";

interface IProps {
	open: boolean;
	onClose: () => void;
	selectedUserIds: number[];
	onLoadingChange: (isLoading: boolean) => void;
}

export const buttonSx = {
	border: "none",
	boxShadow: "none",
	color: "primary.main",
	"&:hover": {
		backgroundColor: palette.orange08
	}
};

export const BulkRescheduleConfirmationModal: FC<IProps> = ({open, onClose, selectedUserIds, onLoadingChange}) => {
	const {t} = useSSTranslation();
	const socket = useContext(SocketContext);

	const handleConfirm = useCallback(() => {
		if (!open || !socket) {
			return;
		}

		const payload: StartRescheduleOverdueModulesPayload = {
			userIds: selectedUserIds
		};

		onLoadingChange(true);

		socket.emit(SOCKET_EVENTS.START_RESCHEDULE_OVERDUE_MODULES, payload, (response: JobStatusCallback) => {
			// Emit custom event directly
			window.dispatchEvent(new CustomEvent("rescheduleOverdueModulesCompleted", {detail: response}));
		});

		// Close modal immediately after emitting the event
		onClose();
	}, [open, socket, selectedUserIds, onLoadingChange, onClose]);

	return (
		<Dialog
			open={open}
			onClose={(event, reason) => {
				if (reason !== "backdropClick") {
					onClose();
				}
			}}
			aria-labelledby="alert-dialog-title"
			aria-describedby="alert-dialog-description"
			PaperProps={{
				sx: {
					bgcolor: "background.paperElevation3",
					borderColor: "background.paperElevation3"
				}
			}}
		>
			<DialogTitle id="alert-dialog-title">
				{t("bulk_reschedule_modal_title", "Reschedule Overdue Lessons")}
			</DialogTitle>
			<DialogContent>
				<DialogContentText id="alert-dialog-description" variant="body1" color="text.primary">
					{t("bulk_reschedule_modal_body_copy", "This will reschedule lessons for all selected users who have overdue assignments.")}
				</DialogContentText>
			</DialogContent>
			<DialogActions>
				<Button variant="text" onClick={onClose} sx={buttonSx}>
					{t("team_progress_calendar_cancel", "Cancel")}
				</Button>
				<Button variant="text" onClick={handleConfirm} sx={buttonSx}>
					{t("common_confirm", "Confirm")}
				</Button>
			</DialogActions>
		</Dialog>
	);
}